import { z, ZodIssueCode, isoZ } from 'my-zod'
import { dateFns } from 'iso-fns2'
import { SalesQuoteSeasonTypeEnum, SalesQuoteSeasonType } from './sales-quote-season-type'
import {
  validateIMXMonthCount,
  validateAllowedSeasonMonthCounts,
  validateVehicleCountBySeason
} from './sales-quote-season-validators'

export const SalesQuoteSeasonProgramPricingPlan = z.enum(['PerMonthPricing', 'PerRidePricing']).setEnumLabels({
  PerMonthPricing: 'Per Month Pricing',
  PerRidePricing: 'Per Ride Pricing'
})

export const salesQuoteSeasonLineItem = z.object({
  id: z.string().uuid(),
  modelFamilyId: z
    .number()
    .describe(
      'The model family for this line item.  Note that ATV model families cannot be added without other ORV models being included.'
    ),
  quantity: z.number().describe('Quantity of vehicles for this model group.'),
  quoteSeasonId: z.string().nullish()
})
export type salesQuoteSeasonLineItem = z.infer<typeof salesQuoteSeasonLineItem>

export const salesQuoteSeasonActivePeriod = z.object({
  id: z.string().uuid(),
  startDate: isoZ.date(),
  numberOfMonths: z.number(),
  quoteSeasonId: z.string().nullish()
})
export type salesQuoteSeasonActivePeriod = z.infer<typeof salesQuoteSeasonActivePeriod>

export const salesQuoteSeasonsModel = z
  .object({
    id: z.string().uuid(),
    headerId: z.number().describe('Associated vehicle order header.'),
    title: z.string().nullable(),
    type: SalesQuoteSeasonTypeEnum.describe(
      'What Polaris Experience holding term type does this vehicle order holding term fall under.'
    ),
    isCustomized: z.boolean(),
    isCustomizedJustification: z.string().nullish(),
    activePeriods: salesQuoteSeasonActivePeriod.array(),
    contractedStartDate: isoZ
      .date()
      .setLabel('Holding Term Start Date')
      .describe(
        'The date this holding term (when vehicle order signed) officially begins for purposes of billing and other contractual obligations.  Should be the 1st of the 15th of the month.  Should not be within 90 days of the vehicle order date.'
      ),
    numberOfMonths: z
      .number()
      .setLabel('Holding Term Length (Months)')
      .describe(
        'The number of months this holding term runs before ending for purposes of billing and other contractual obligations.'
      ),
    timelineJustification: z.string().nullable(),
    targetVehicleDeliveryDate: isoZ
      .date()
      .describe('Targeted date for vehicles to arrive at outfitter shipping address.')
      .nullable(), // TODO: candidate to be retired
    earliestVehicleDeliveryDate: isoZ
      .date()
      .describe('Earliest possible date for vehicles to arrive at outfitter shipping address.'),
    preferredPickupDate: isoZ.date().nullable(),
    lineItems: salesQuoteSeasonLineItem.array(),
    rideFee: z.number().nullable(),
    programPricingPlan: SalesQuoteSeasonProgramPricingPlan, // TODO: if this new name appears to be the long-term name then it can be renamed to 'vehicleOrderPricingPlan'
    programFeePerMonth: z.number().nullable(), // TODO: if this new name appears to be the long-term name then it can be renamed to 'useFeePerMonth'
    programFeePerRide: z
      .number()
      .nullable()
      .describe(
        `The 'Use Fee Per Ride' is charged per ride, with a minimum charge of the 'Minimum Monthly Use Fee' regardless of actual ride count.`
      ), // TODO: if this new name appears to be the long-term name then it can be renamed to 'useFeePerRide'
    minimumProgramFeePerMonth: z.number().nullable(), // TODO: if this new name appears to be the long-term name then it can be renamed to 'minimumUseFeePerMonth'
    createdDate: isoZ.instant()
    // we can't omit it in systemView because we have superRefine
    // serverLastModifiedDate: isoZ.instant()
  })
  .superRefine((season, ctx) => {
    const seasonsWith1ActivePeriod: SalesQuoteSeasonType[] = [
      'ATV',
      'CUSTOM',
      'IMX',
      'ORV_PEAK',
      'ORV',
      'SNO',
      'SNO_PERFORMANCE'
    ]
    if (season.activePeriods.length > 1 && seasonsWith1ActivePeriod.includes(season.type)) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: `${season.type} cannot have more than 1 active period in a holding term`,
        path: ['activePeriods', 1]
      })
    }
    if (season.activePeriods.length > 4 && season.type === 'SLG') {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: `${season.type} cannot have more than 4 active periods in a holding term`,
        path: ['activePeriods', 4]
      })
    }

    // holding term start date should always be either the 1st or 15th of the month
    if (dateFns.getDay(season.contractedStartDate) !== 1 && dateFns.getDay(season.contractedStartDate) !== 15) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: `Each holding term should start on either the 1st or 15th of the month.`,
        path: ['contractedStartDate']
      })
    }

    // if customized, need justification
    if (season.isCustomized && !season.isCustomizedJustification) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: `Please explain why this holding term should be customized.`,
        path: ['isCustomizedJustification']
      })
    }

    if (season.programPricingPlan === 'PerMonthPricing' && season.programFeePerRide) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: `Cannot specify a programFeePerRide and a price per month.`,
        path: ['minimumProgramFeePerMonth']
      })
    }
    if (season.programPricingPlan === 'PerMonthPricing' && season.minimumProgramFeePerMonth) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: `Cannot specify a minimum monthly use fee and a price per month.`,
        path: ['minimumProgramFeePerMonth']
      })
    }

    if (season.lineItems.some((line) => !line.quantity)) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: 'Value should be greater than or equal to 1',
        path: ['lineItems']
      })
    }

    validateIMXMonthCount(season, ctx)
  })
  // It is necessary to support the old pricing matrix options
  .superRefine((season, ctx) => {
    validateVehicleCountBySeason(season, ctx)
    validateAllowedSeasonMonthCounts(season, ctx)
  })

export type salesQuoteSeasonsModel = z.infer<typeof salesQuoteSeasonsModel>
